import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { Reveal } from '@/components/Reveal';

const globalCategories = [
  {
    id: 'business-support',
    title: 'Business Support',
    description: 'Administrative and operational support that keeps your business running with precision and consistency.',
    services: [
      { title: 'Administrative Support', desc: 'Email management, scheduling, documentation and business administration.', slug: 'administrative-support' },
      { title: 'Back Office Operations', desc: 'Data entry, document management, order processing and operational tasks.', slug: 'back-office-operations' },
      { title: 'Virtual Assistance', desc: 'Dedicated remote support for executives, directors and business owners.', slug: 'virtual-assistance' },
      { title: 'Business Operations Support', desc: 'Project coordination, vendor management and workflow operations.', slug: 'business-operations' },
    ],
  },
  {
    id: 'customer-operations',
    title: 'Customer Operations',
    description: 'Professional, brand-aligned customer support that represents your business with consistency at every touchpoint.',
    services: [
      { title: 'Customer Support', desc: 'Email, chat and ticket-based support across all customer touchpoints.', slug: 'customer-support' },
      { title: 'CRM Support', desc: 'CRM data management, pipeline tracking and client relationship operations.', slug: 'crm-support' },
    ],
  },
  {
    id: 'corporate-functions',
    title: 'Corporate Functions',
    description: 'Finance, accounting and HR support that keeps your corporate operations accurate, compliant and professionally managed.',
    services: [
      { title: 'Finance & Accounting', desc: 'Bookkeeping, accounts payable/receivable, reconciliation and financial reporting.', slug: 'finance-accounting' },
      { title: 'HR Support', desc: 'Employee records, onboarding coordination, recruitment administration and HR reporting.', slug: 'human-resources' },
    ],
  },
  {
    id: 'specialist-operations',
    title: 'Specialist Operations',
    description: 'Sector-specific operational support for healthcare, e-commerce and research-intensive businesses.',
    services: [
      { title: 'Healthcare Administration', desc: 'Patient communication, records management and specialist healthcare back-office support.', slug: 'healthcare-administration' },
      { title: 'Business Research', desc: 'Market research, competitor intelligence and business research for strategic decisions.', slug: 'business-research' },
    ],
  },
];

const regions = [
  { name: 'United Kingdom', flag: '🇬🇧' },
  { name: 'United States', flag: '🇺🇸' },
  { name: 'Canada', flag: '🇨🇦' },
  { name: 'Europe', flag: '🇪🇺' },
  { name: 'Australia', flag: '🇦🇺' },
  { name: 'New Zealand', flag: '🇳🇿' },
];

export default function GlobalSolutions() {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Global — Outsourcing & Business Solutions | NKP VENTURES"
        description="NKP VENTURES provides professional outsourcing and business process support for companies across the UK, US, Canada, Europe, Australia and New Zealand."
        canonical="https://nkpventures.com/solutions/global"
      />

      {/* Hero */}
      <section className="bg-[#0F2847] pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="inline-block bg-white/20 text-white text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full mb-6">Global</span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Outsourcing & Business Solutions
              <br />
              <span className="text-[#5CC8C3]">for International Businesses.</span>
            </h1>
            <p className="text-lg text-white/75 max-w-2xl leading-relaxed mb-10">
              Providing reliable outsourcing and operational support to businesses across international markets — with the professional standards, time zone discipline and communication quality your clients expect.
            </p>
            <div className="flex flex-wrap gap-3 mb-10">
              {regions.map((r) => (
                <span key={r.name} className="bg-white/10 text-white/80 text-sm px-3 py-1.5 rounded-full">
                  {r.flag} {r.name}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0E7B73] hover:bg-[#0F9088] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Get in Touch <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-medium px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                All Solutions
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 bg-[#F7FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="mb-14">
              <p className="overline-label text-[#0E5A57] mb-3">Our Global Offering</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628]">
                Four categories. Full operational coverage.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {globalCategories.map((cat, i) => (
              <Reveal key={cat.id} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-[#E5E7EB] hover:border-[#0E5A57]/30 hover:shadow-lg transition-all duration-300 h-full">
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#0E5A57] bg-teal-50 px-3 py-1 rounded-full">
                    {cat.title}
                  </span>
                  <p className="text-[#374151] text-sm leading-relaxed mt-4 mb-6">{cat.description}</p>
                  <div className="space-y-3">
                    {cat.services.map((svc) => (
                      <div key={svc.title} className="flex gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#0E5A57] mt-2 flex-shrink-0" />
                        <div>
                          <Link
                            to={`/services/${svc.slug}`}
                            className="text-[#0A1628] font-semibold text-sm hover:text-[#0E5A57] hover:underline transition-colors"
                          >
                            {svc.title}
                          </Link>
                          <p className="text-[#6B7280] text-xs mt-0.5 leading-snug">{svc.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 text-center">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold hover:underline text-sm"
              >
                View all individual service pages <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why NKP Global */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-3">Why NKP VENTURES</p>
              <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#0A1628] mb-6">
                International fluency. Operational discipline.
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-8">
                We serve clients across the UK, US, Canada, Europe, Australia and New Zealand — with the communication standards, time zone discipline and cultural fluency that international businesses expect from a trusted operational partner.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-[#0F2847] hover:bg-[#0A1628] text-white font-semibold px-7 py-3.5 rounded-full transition-colors duration-200"
              >
                Start a Conversation <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="space-y-4">
                {[
                  'Confidentiality-first approach with comprehensive NDAs on every engagement',
                  'Documented, auditable workflows built for consistency and quality',
                  'Time zone-aligned support for UK, US, Canada, Europe, AU & NZ',
                  'Professional communication standards that represent your brand',
                  'Scalable capacity without the overhead of full-time employment',
                  'Long-term partnership approach — not transactional outsourcing',
                ].map((point) => (
                  <li key={point} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-[#0E5A57] mt-0.5 flex-shrink-0" />
                    <span className="text-[#374151] leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F2847]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl font-bold text-white mb-4">
              Ready to strengthen your operations?
            </h2>
            <p className="text-white/70 mb-8">
              Tell us about your business and what you need. We'll respond within one business day.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-[#0E7B73] hover:bg-[#0F9088] text-white font-semibold px-8 py-4 rounded-full transition-colors duration-200"
            >
              Let's Talk <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
